import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Button, Modal, Progress, Radio, Select, Spin,
} from 'antd';
import {
  ArrowRight, Check, CheckCircle, Warning, WarningCircle,
} from '@phosphor-icons/react';
import csrfHeaders from '../../../utils/csrfHeaders';
import { captureAnalytics } from '../../../actions/preload';
import './category-merge-wizard.scss';

const POLL_INTERVAL_MS = 2000;
// Consecutive failed status checks before telling the admin; polling continues.
const POLL_FAILURES_BEFORE_NOTICE = 3;
const ACTIVE_STATES = ['queued', 'running'];
const STEP_TARGET = 0;
const STEP_REVIEW = 1;
const STEP_PROGRESS = 2;
const STEP_TITLES = ['Choose', 'Review', 'Merge'];

const errorMessage = (error, fallback) => error.response?.data?.error || fallback;

const formatCount = (count, noun) => `${count.toLocaleString()} ${noun}${count === 1 ? '' : 's'}`;

const targetLabel = (category) => {
  const aiName = category.ai_category_name;
  if (!aiName || aiName === category.emr_category) return category.emr_category;
  return `${category.emr_category} (AI: ${aiName})`;
};

const renderTargetOption = (category) => (
  <Select.Option key={category.id} value={category.id}>
    {targetLabel(category)}
    {!category.enabled && <span className="category-merge-wizard__muted"> · off</span>}
  </Select.Option>
);

const NOTICE_ICONS = { error: WarningCircle, warning: Warning };

const Notice = ({ tone, children }) => {
  const Icon = NOTICE_ICONS[tone];
  return (
    <div className={`category-merge-wizard__notice category-merge-wizard__notice--${tone}`} role={tone === 'error' ? 'alert' : undefined}>
      <Icon size={16} weight="fill" className="category-merge-wizard__notice-icon" />
      <div>{children}</div>
    </div>
  );
};

Notice.propTypes = {
  tone: PropTypes.oneOf(['error', 'warning']).isRequired,
  children: PropTypes.node.isRequired,
};

// Merges a manual-only category into a catalog-linked AI category: pick the AI
// category, review what moves (and anything blocking it), then watch the
// background merge finish. The server owns every rule; this only renders the
// plan it returns.
const CategoryMergeWizard = ({
  source, aiCategories, suggestedTargetIds, onClose, onStarted, onMerged,
}) => {
  const [step, setStep] = useState(STEP_TARGET);
  const [targetId, setTargetId] = useState(suggestedTargetIds[0] || null);
  // null until the first preview: the server picks the default from which
  // name the org's EMR uploads actually use.
  const [finalName, setFinalName] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState(null);
  const mounted = useRef(true);
  const pollTimer = useRef(null);
  const pollFailures = useRef(0);

  useEffect(() => () => {
    mounted.current = false;
    clearTimeout(pollTimer.current);
  }, []);

  const base = `/api/v1/document_categories/${source.id}`;

  const loadPlan = useCallback(async (nextTargetId, nextFinalName) => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${base}/merge_preview`, {
        params: { target_id: nextTargetId, final_name: nextFinalName },
      });
      if (!mounted.current) return false;
      setPlan(response.data.plan);
      setFinalName(response.data.plan.final_name);
      return true;
    } catch (err) {
      if (mounted.current) setError(errorMessage(err, 'Could not preview this merge. Please try again.'));
      return false;
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [base]);

  const pollStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${base}/merge_status`);
      if (!mounted.current) return;
      if (pollFailures.current > 0) {
        pollFailures.current = 0;
        setError('');
      }
      const next = response.data.status;
      setStatus(next);
      if (next.state === 'succeeded') {
        captureAnalytics('category_merge_succeeded', { moved: next.moved });
        onMerged();
        return;
      }
      if (next.state === 'failed') return;
    } catch (err) {
      if (!mounted.current) return;
      // A missing status won't come back; anything else is likely a blip.
      if (err.response?.status === 404) {
        setError('Lost track of the merge. Refresh the page to see its result.');
        return;
      }
      pollFailures.current += 1;
      if (pollFailures.current >= POLL_FAILURES_BEFORE_NOTICE) {
        setError('Having trouble checking on the merge. Still trying…');
      }
    }
    pollTimer.current = setTimeout(pollStatus, POLL_INTERVAL_MS);
  }, [base, onMerged]);

  // Pick up a merge that is already running, e.g. the wizard was closed
  // mid-merge and reopened. No status (404) just means a fresh merge.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${base}/merge_status`).then((response) => {
      const current = response.data.status;
      if (cancelled || !ACTIVE_STATES.includes(current?.state)) return;
      // Restore what was submitted so "Review again" after a failure
      // previews the same merge.
      setTargetId(current.target_id);
      setFinalName(current.final_name);
      setStatus(current);
      setStep(STEP_PROGRESS);
      onStarted();
      pollTimer.current = setTimeout(pollStatus, POLL_INTERVAL_MS);
    }).catch(() => {});
    return () => {
      cancelled = true;
      clearTimeout(pollTimer.current);
    };
  }, [base, onStarted, pollStatus]);

  const goToReview = async () => {
    captureAnalytics('category_merge_review_opened', {
      document_category_id: source.id, target_category_id: targetId, retry: step === STEP_PROGRESS,
    });
    // Keep a failed status until the preview loads, so a preview error
    // still leaves "Review again" on screen.
    if (await loadPlan(targetId, finalName)) {
      setStatus(null);
      setStep(STEP_REVIEW);
    }
  };

  const goBack = () => {
    captureAnalytics('category_merge_back_clicked', { document_category_id: source.id });
    setStep(STEP_TARGET);
  };

  const close = () => {
    captureAnalytics('category_merge_wizard_closed', {
      document_category_id: source.id, step: STEP_TITLES[step].toLowerCase(), state: status?.state || null,
    });
    onClose();
  };

  // The name options are the two categories' names, so a new target goes back
  // to the server's default.
  const changeTarget = (id) => {
    captureAnalytics('category_merge_target_changed', {
      document_category_id: source.id, target_category_id: id, suggested: suggestedTargetIds.includes(id),
    });
    setTargetId(id);
    setFinalName(null);
  };

  // Keep the chosen name in step with the plan on screen: if its preview
  // fails, go back to the name the visible plan was built for.
  const changeName = async (event) => {
    const previousName = finalName;
    const option = plan.name_options.find((nameOption) => nameOption.name === event.target.value);
    captureAnalytics('category_merge_name_changed', { document_category_id: source.id, kept: option?.row });
    setFinalName(event.target.value);
    if (!(await loadPlan(targetId, event.target.value)) && mounted.current) setFinalName(previousName);
  };

  // Submits the plan on screen rather than the live selections, so the
  // merge is always the one the admin reviewed.
  const startMerge = async () => {
    setLoading(true);
    setError('');
    captureAnalytics('category_merge_started', { records: plan.source.record_count });
    try {
      const response = await axios.post(
        `${base}/merge`,
        { target_id: plan.target.id, final_name: plan.final_name },
        { headers: csrfHeaders() },
      );
      if (!mounted.current) return;
      setStatus(response.data.status);
      setStep(STEP_PROGRESS);
      onStarted();
      pollTimer.current = setTimeout(pollStatus, POLL_INTERVAL_MS);
    } catch (err) {
      if (mounted.current) setError(errorMessage(err, 'Could not start the merge. Please try again.'));
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  const running = step === STEP_PROGRESS && ACTIVE_STATES.includes(status?.state);
  const succeeded = status?.state === 'succeeded';
  const failed = status?.state === 'failed';

  const suggestedIds = new Set(suggestedTargetIds);
  const suggested = aiCategories.filter((category) => suggestedIds.has(category.id));
  const others = aiCategories.filter((category) => !suggestedIds.has(category.id));

  const renderTargetStep = () => (
    <>
      <p className="category-merge-wizard__lead">
        Link
        {' '}
        <strong>{source.emr_category}</strong>
        {' '}
        to the AI category that means the same thing. The AI then files those
        documents into one category, which keeps the EMR category name you choose
        next. The documents, rules and settings from
        {' '}
        <strong>{source.emr_category}</strong>
        {' '}
        move over and the manual-only category is removed.
      </p>
      <div className="category-merge-wizard__label" id="category-merge-target-label">AI category</div>
      <Select
        aria-labelledby="category-merge-target-label"
        className="category-merge-wizard__select"
        showSearch
        optionFilterProp="children"
        placeholder="Choose an AI category"
        value={targetId}
        onChange={changeTarget}
        disabled={loading}
      >
        {suggested.length > 0 && (
          <Select.OptGroup label="Suggested">{suggested.map(renderTargetOption)}</Select.OptGroup>
        )}
        {suggested.length > 0
          ? <Select.OptGroup label="All AI categories">{others.map(renderTargetOption)}</Select.OptGroup>
          : others.map(renderTargetOption)}
      </Select>
      <div className="category-merge-wizard__muted category-merge-wizard__hint">
        Don’t see it? Turn the AI category on in the list above first.
      </div>
    </>
  );

  const renderReviewStep = () => {
    if (!plan) return <Spin />;
    return (
      <>
        <div className="category-merge-wizard__mapping" data-testid="category-merge-mapping">
          <div className="category-merge-wizard__mapping-cell">
            <div className="category-merge-wizard__eyebrow">Your category</div>
            <strong>{plan.source.emr_category}</strong>
            <div className="category-merge-wizard__muted">{`${formatCount(plan.source.record_count, 'document')} · manual-only`}</div>
          </div>
          <ArrowRight size={16} className="category-merge-wizard__arrow" aria-hidden="true" />
          <div className="category-merge-wizard__mapping-cell">
            <div className="category-merge-wizard__eyebrow">AI category</div>
            <strong>{plan.target.ai_category_name}</strong>
            <div className="category-merge-wizard__muted">{formatCount(plan.target.record_count, 'document')}</div>
          </div>
          <ArrowRight size={16} className="category-merge-wizard__arrow" aria-hidden="true" />
          <div className="category-merge-wizard__mapping-cell category-merge-wizard__mapping-cell--result">
            <div className="category-merge-wizard__eyebrow">EMR category</div>
            <strong>{plan.final_name}</strong>
            <div className="category-merge-wizard__muted">what your team and EMR see</div>
          </div>
        </div>
        <p className="category-merge-wizard__mapping-caption">
          {`The AI will classify these documents as “${plan.target.ai_category_name}”. `}
          {`In Medsender and in your EMR they’ll be filed as “${plan.final_name}”.`}
        </p>

        {plan.name_options.length > 1 && (
          <div className="category-merge-wizard__section">
            <div className="category-merge-wizard__label">EMR category to keep</div>
            <Radio.Group
              className="category-merge-wizard__names"
              value={finalName}
              onChange={changeName}
              disabled={loading}
            >
              {plan.name_options.map((nameOption) => (
                <Radio key={nameOption.name} value={nameOption.name}>
                  <span className="category-merge-wizard__name">{nameOption.name}</span>
                  <span className="category-merge-wizard__muted">
                    {nameOption.row === 'manual' ? 'Your team’s category' : 'The AI category’s current name'}
                    {` · ${formatCount(nameOption.upload_count, 'EMR upload')} in the last 30 days`}
                    {nameOption.automatic_upload_count > 0 ? ` (${nameOption.automatic_upload_count.toLocaleString()} automatic)` : ''}
                  </span>
                </Radio>
              ))}
            </Radio.Group>
          </div>
        )}

        {plan.blockers.map((blocker) => <Notice key={blocker} tone="error">{blocker}</Notice>)}
        {plan.warnings.map((warning) => <Notice key={warning} tone="warning">{warning}</Notice>)}

        {plan.mergeable && (
          <div className="category-merge-wizard__section">
            <div className="category-merge-wizard__label">What happens</div>
            <ul className="category-merge-wizard__changes">
              {plan.changes.map((change) => (
                <li key={change}>
                  <Check size={14} weight="bold" className="category-merge-wizard__change-icon" aria-hidden="true" />
                  <span>{change}</span>
                </li>
              ))}
            </ul>
            <p className="category-merge-wizard__footnote">
              Moving documents does not re-upload anything to your EMR, re-run
              auto-upload, re-route to teams or send notifications. This can’t be undone.
            </p>
          </div>
        )}
      </>
    );
  };

  const renderProgressStep = () => {
    const total = status?.total || 0;
    const moved = status?.moved || 0;
    if (succeeded) {
      return (
        <div className="category-merge-wizard__result">
          <CheckCircle size={40} weight="fill" className="category-merge-wizard__result-icon" />
          <div className="category-merge-wizard__result-title">{`Merged into “${status.final_name}”`}</div>
          <p className="category-merge-wizard__result-body">
            {`${formatCount(moved, 'document')} moved and the manual-only “${source.emr_category}” category was removed.`}
            {plan && ` Documents the AI classifies as “${plan.target.ai_category_name}” are now filed as “${status.final_name}”.`}
          </p>
        </div>
      );
    }
    if (failed) {
      return (
        <Notice tone="error">
          <strong>The merge didn’t finish</strong>
          <div>{status.error}</div>
        </Notice>
      );
    }
    return (
      <div className="category-merge-wizard__progress">
        <Progress percent={total ? Math.floor((moved / total) * 100) : 0} status="active" />
        <div className="category-merge-wizard__muted">
          {`Moving ${formatCount(total, 'document')}… You can close this window; the merge keeps running.`}
        </div>
      </div>
    );
  };

  const footer = () => {
    if (step === STEP_TARGET) {
      return [
        <Button key="cancel" onClick={close}>Cancel</Button>,
        <Button key="next" type="primary" disabled={!targetId} loading={loading} onClick={goToReview}>
          Next
        </Button>,
      ];
    }
    if (step === STEP_REVIEW) {
      return [
        <Button key="back" onClick={goBack}>Back</Button>,
        <Button
          key="merge"
          type="primary"
          danger
          disabled={!plan?.mergeable}
          loading={loading}
          onClick={startMerge}
        >
          Merge categories
        </Button>,
      ];
    }
    if (failed) {
      return [
        <Button key="close" onClick={close}>Close</Button>,
        <Button key="retry" type="primary" onClick={goToReview}>Review again</Button>,
      ];
    }
    return [<Button key="done" type="primary" onClick={close}>{running ? 'Close' : 'Done'}</Button>];
  };

  const stepState = (index) => {
    if (index < step || (index === step && succeeded)) return 'done';
    if (index === step) return failed ? 'failed' : 'current';
    return 'upcoming';
  };

  return (
    <Modal
      visible
      width={640}
      title="Merge into an AI category"
      onCancel={close}
      maskClosable={false}
      footer={footer()}
      className="category-merge-wizard"
      wrapClassName="settings-modal-wrap"
    >
      <ol className="category-merge-wizard__steps" aria-label="Merge steps">
        {STEP_TITLES.map((title, index) => {
          const state = stepState(index);
          return (
            <li
              key={title}
              className={`category-merge-wizard__step category-merge-wizard__step--${state}`}
              aria-current={state === 'current' ? 'step' : undefined}
            >
              <span className="category-merge-wizard__step-dot">
                {state === 'done' ? <Check size={12} weight="bold" /> : index + 1}
              </span>
              {title}
            </li>
          );
        })}
      </ol>
      {error && <Notice tone="error">{error}</Notice>}
      {step === STEP_TARGET && renderTargetStep()}
      {step === STEP_REVIEW && renderReviewStep()}
      {step === STEP_PROGRESS && renderProgressStep()}
    </Modal>
  );
};

CategoryMergeWizard.propTypes = {
  source: PropTypes.shape({
    id: PropTypes.number.isRequired,
    emr_category: PropTypes.string.isRequired,
  }).isRequired,
  aiCategories: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    emr_category: PropTypes.string.isRequired,
    ai_category_name: PropTypes.string,
    enabled: PropTypes.bool,
  })).isRequired,
  suggestedTargetIds: PropTypes.arrayOf(PropTypes.number),
  onClose: PropTypes.func.isRequired,
  onStarted: PropTypes.func,
  onMerged: PropTypes.func.isRequired,
};

CategoryMergeWizard.defaultProps = {
  suggestedTargetIds: [],
  onStarted: () => {},
};

export default CategoryMergeWizard;
